import { Injectable } from "@angular/core";
import {
  DecisionConfidenceCompact, DecisionDetail, DecisionMissingInformation, DecisionPriorityCompact,
  DecisionRiskCompact, DecisionState
} from "../../models/home";
import { LanguageService } from "../language/language.service";
import { HomeDecisionRiskDisplayBuilder } from "./home-decision-display-ux.service.risk-and-missing-information";
import { HomeDecisionTechnicalTraceBuilder } from "./home-decision-display-ux.service.technical-trace";

export type DecisionPriorityTone = "critical" | "high" | "medium" | "low";
export type DecisionRiskTone = DecisionPriorityTone | "none";
export type DecisionConfidenceTone = "high" | "medium" | "low";
export type MissingInformationResolutionLocation =
  | "documentOrigin" | "systemOrigin" | "classification" | "manualReview" | "undetermined";
export interface DecisionPriorityDisplay {
  label: string; summary: string; score: number; scoreLabel: string; conciseLabel: string; tone: DecisionPriorityTone;
}
export interface DecisionRiskDisplay { label: string; summary: string; tone: DecisionRiskTone; }
export interface DecisionConfidenceDisplay {
  label: string; summary: string; percentage: number; percentageLabel: string; tone: DecisionConfidenceTone;
}
export interface DecisionMissingInformationDisplay {
  label: string; blockingReason: string; resolutionLocation: MissingInformationResolutionLocation;
  resolutionLocationLabel: string; resolutionCopy: string; externalResolutionCopy: string; nextStepCopy: string;
}
export interface DecisionTechnicalEntry { key: string; label: string; value: unknown; }

@Injectable({ providedIn: "root" })
export class HomeDecisionDisplayUxService {
  private readonly dueSoonMs = 1000 * 60 * 60 * 72;
  private readonly riskDisplay: HomeDecisionRiskDisplayBuilder;
  private readonly trace: HomeDecisionTechnicalTraceBuilder;

  constructor(private readonly lang: LanguageService) {
    this.riskDisplay = new HomeDecisionRiskDisplayBuilder(lang);
    this.trace = new HomeDecisionTechnicalTraceBuilder(lang);
  }

  getPriorityDisplay(priority: DecisionPriorityCompact, state: DecisionState, dueAt?: string): DecisionPriorityDisplay {
    const tone = this.getPriorityTone(priority, state, dueAt);
    const label = this.lang.translate(`home.decisionDisplay.priority.${tone}.label`);
    const score = Math.max(0, Math.round(priority.score ?? 0));
    return {
      label, summary: this.lang.translate(`home.decisionDisplay.priority.${tone}.copy`),
      score, scoreLabel: `${score}/100`, conciseLabel: `${label} · ${score}`, tone
    };
  }
  getPriorityBadgeLabel(priority: DecisionPriorityCompact, state: DecisionState, dueAt?: string): string {
    return this.getPriorityDisplay(priority, state, dueAt).conciseLabel;
  }
  getRiskDisplay(risk: DecisionRiskCompact, state: DecisionState): DecisionRiskDisplay { return this.riskDisplay.getRiskDisplay(risk, state); }
  getConfidenceDisplay(confidence: DecisionConfidenceCompact): DecisionConfidenceDisplay { return this.riskDisplay.getConfidenceDisplay(confidence); }
  getMissingInformationDisplay(item: DecisionMissingInformation): DecisionMissingInformationDisplay {
    return this.riskDisplay.getMissingInformationDisplay(item);
  }
  getRiskTone(risk: DecisionRiskCompact, state: DecisionState): DecisionRiskTone { return this.riskDisplay.getRiskTone(risk, state); }
  getResolutionCopy(location: MissingInformationResolutionLocation): string { return this.riskDisplay.getResolutionCopy(location); }
  normalizeUserFacingReason(reason?: string | null): string | null { return this.riskDisplay.normalizeUserFacingReason(reason); }
  looksTechnical(value: string): boolean { return this.riskDisplay.looksTechnical(value); }
  ensureSentence(value: string): string { return this.riskDisplay.ensureSentence(value); }
  humanizeFieldName(value?: string | null): string | null { return this.riskDisplay.humanizeFieldName(value); }
  getTechnicalEntries(detail: DecisionDetail | null): readonly DecisionTechnicalEntry[] { return this.trace.getTechnicalEntries(detail); }
  getTraceLabel(key: string): string { return this.trace.getTraceLabel(key); }
  getTechnicalSignalLabel(group: "priority" | "risk" | "confidence", field: string): string {
    return this.trace.getTechnicalSignalLabel(group, field);
  }
  getTechnicalFieldLabel(field: string): string { return this.trace.getTechnicalFieldLabel(field); }
  isEmptyTechnicalValue(value: unknown): boolean { return this.trace.isEmptyTechnicalValue(value); }
  humanizeTechnicalLabel(label: string): string { return this.trace.humanizeTechnicalLabel(label); }
  normalizeTechnicalField(value: string): string { return this.trace.normalizeTechnicalField(value); }
  translateKnownKey(key: string): string | null { return this.trace.translateKnownKey(key); }

  private getPriorityTone(priority: DecisionPriorityCompact, state: DecisionState, dueAt?: string): DecisionPriorityTone {
    const dueAtMs = dueAt ? Date.parse(dueAt) : Number.NaN;
    const now = Date.now();
    if ((!Number.isNaN(dueAtMs) && dueAtMs < now) || priority.score >= 90) return "critical";
    if (state === "act_now" || (!Number.isNaN(dueAtMs) && dueAtMs - now <= this.dueSoonMs) || priority.score >= 75) return "high";
    if (state === "act_soon" || state === "waiting" || priority.score >= 45) return "medium";
    return "low";
  }
}
